import {
  AuthProvider,
  NetworkProvider as ToolkitNetworkProvider,
  ApiClient,
} from '@locus-taxy/encore-ui-network';
import React from 'react';
import { useAuth0 } from '../../auth';
import { registerEndpoints } from './endpoints';
import { getApiBaseUrl } from '../../config';
import { useAuthIntegration } from './auth';
import { isRunningInWebView } from './nativeBridge';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Box } from '@locus-taxy/encore-mui';
import LoginPage from '../../components/LoginPage';
import type { InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { useUser } from '../../hooks/useUser';
import { useClientId } from '../../hooks/useClientId';

type EncoreRequestConfig = InternalAxiosRequestConfig & {
  requireAuth?: boolean;
};

interface NetworkProviderProps {
  children: React.ReactNode;
  baseUrl?: string;
}

export const NetworkProvider: React.FC<NetworkProviderProps> = ({
  children,
  baseUrl: baseUrlProp,
}) => {
  const { isNetworkAuthReady } = useAuthIntegration();
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const inWebView = isRunningInWebView();
  const { user } = useUser();
  const clientId = useClientId();
  // Construct dynamic base URL using clientId
  // Use prop if provided (for backward compatibility), otherwise construct dynamically
  const baseUrl = React.useMemo(() => {
    if (baseUrlProp) {
      return baseUrlProp;
    }
    try {
      // getApiBaseUrl handles the case where clientId is missing by providing a fallback
      return getApiBaseUrl(clientId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[NetworkProvider] Failed to construct API base URL:',
        error instanceof Error ? error.message : error
      );
      // Return null as safe fallback - component will handle this case
      return null;
    }
  }, [baseUrlProp, clientId]);

  const onBehalfOf = React.useMemo(() => {
    const userId = user?.userId;
    if (!userId) return 'anonymous';
    const organizationId = user?.homeTenant;
    if (!organizationId) return 'anonymous';
    return `${organizationId}/${userId}`;
  }, [user]);
  // Register endpoints synchronously
  registerEndpoints();

  // Install request interceptor (v3 ApiClient.addRequestInterceptor) to fetch a fresh Auth0 token per request in browser
  React.useEffect(() => {
    if (inWebView) return; // skip for native bridge/webview
    if (!baseUrl) return; // Wait for baseUrl to be constructed

    const client = ApiClient.getInstance(baseUrl);
    // Use the new v3 interceptor API
    const interceptorId = client.addRequestInterceptor(
      async (config: EncoreRequestConfig) => {
        try {
          // Always set On-Behalf-Of header
          config.headers = {
            ...config.headers,
            'On-Behalf-Of': onBehalfOf,
          } as unknown as AxiosRequestHeaders;

          const requireAuth = config.requireAuth !== false;
          if (requireAuth) {
            const freshToken = await getAccessTokenSilently();
            if (freshToken) {
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${freshToken}`,
              } as unknown as AxiosRequestHeaders;
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching access token:', error);
        }
        return config;
      }
    );

    return () => {
      try {
        client.ejectRequestInterceptor(interceptorId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error ejecting request interceptor:', error);
      }
    };
  }, [inWebView, baseUrl, getAccessTokenSilently, onBehalfOf]);

  // Don't render until auth is ready
  if (
    (inWebView && !isNetworkAuthReady) ||
    (!inWebView && (isAuthLoading || (isAuthenticated && !isNetworkAuthReady)))
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <LoadingSpinner showRandomMessage={false} />
      </Box>
    );
  }

  // Don't render until we have a baseUrl (clientId is available)
  // Only show spinner if we ARE authenticated and missing a real clientId,
  // because if NOT authenticated, we want to show the LoginPage
  if (!clientId && !inWebView && isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <LoadingSpinner showRandomMessage={false} />
      </Box>
    );
  }

  // Handle case where baseUrl construction failed (missing apiBaseDomain config)
  if (!baseUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            color: 'error.main',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Box sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}>
            Configuration Error
          </Box>
          <Box>
            Missing API base URL configuration. Please check your environment
            configuration file.
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <ToolkitNetworkProvider>
      <AuthProvider baseUrl={baseUrl}>
        {!inWebView && !isAuthenticated ? <LoginPage /> : children}
      </AuthProvider>
    </ToolkitNetworkProvider>
  );
};
